import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Sentry from '@sentry/react';

import { createCardTokenPagarmeV5 } from 'app/helpers/payment/cardTokenization';
import { useRefreshSubscriptionStatus } from 'app/hooks/useRefreshSubscriptionStatus';
import type { JusgptLimitFunnel } from 'app/hooks/usePlanExploration/types';
import { trackJusGPTUpgradeCompleted } from 'app/pages/JusGPT/helpers/trackJusGPTEvents';
import {
  consumeJusGPTDaysUntilUpgrade,
  notifyJusGPTUpgradeSuccess,
} from 'app/pages/JusGPT/hooks/useJusGPTQuota';
import { tracking } from 'app/services/tracking';
import {
  trackPlanUpgraded,
  trackUpgradeCardTokenizationError,
  trackUpgradeCardTokenizationSuccess,
  trackUpgradePaymentFailed,
  trackUpgradePaymentSuccess,
} from 'app/services/tracking/payments/trackEvents';
import type { CreditCardValidationValues } from 'app/schemas/creditCardValidation';
import type {
  ProductSelected,
  UpgradeTrackingProperties,
} from 'app/hooks/useSubscriptionPlans/selectNewTierPlan';

import { trackCheckoutSubscribeClicked } from '../trackPlanAndPaymentEvents';
import { fetchSubscriptionPollingStatus } from './api';
import type { CheckoutPaymentMethodMode, CheckoutSavedCard } from './useCheckoutPaymentMethod';

const POLL_MAX_DURATION_MS = 300 * 1000;
const TOKENIZATION_TIMEOUT_MS = 30 * 1000;

const captureCheckoutException = (
  flow: string,
  error: unknown,
  extras: Record<string, unknown> = {},
): void => {
  Sentry.withScope((scope) => {
    scope.setTag('area', 'upgrade_subscription');
    scope.setTag('flow', flow);
    scope.setTag('provider', 'pagarme_v5');
    Object.entries(extras).forEach(([key, value]) => {
      scope.setExtra(key, value);
    });
    const sanitized = new Error((error as { message?: string })?.message || 'Checkout failed');
    sanitized.name = (error as { name?: string })?.name || 'Error';
    Sentry.captureException(sanitized);
  });
};

const sanitizeCreateSubscriptionResponseForSentry = (
  response: CreateSubscriptionResponse,
): Record<string, unknown> => ({
  status: response?.status,
  dataStatus: response?.data?.status,
  hasSubscriptionId: Boolean(response?.data?.id),
});

const tokenizeCardWithTimeout: typeof createCardTokenPagarmeV5 = (cardData) =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error('Tempo excedido ao processar o cartão'));
    }, TOKENIZATION_TIMEOUT_MS);

    createCardTokenPagarmeV5(cardData).then(
      (result) => {
        clearTimeout(timer);
        resolve(result);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });

const getPollIntervalMs = (elapsedMs: number): number => {
  if (elapsedMs <= 60 * 1000) {
    return 5000;
  }
  if (elapsedMs <= 120 * 1000) {
    return 10000;
  }
  return 20000;
};

type PollOutcome =
  | { outcome: 'success'; isTrial: boolean }
  | { outcome: 'failed' }
  | { outcome: 'error' };

const pollSubscriptionStatus = async (
  subscriptionId: string,
  authToken: string,
  isCancelled: () => boolean,
): Promise<PollOutcome | null> => {
  const startTime = Date.now();

  while (Date.now() - startTime < POLL_MAX_DURATION_MS) {
    if (isCancelled()) {
      return null;
    }

    try {
      const data = await fetchSubscriptionPollingStatus(
        subscriptionId,
        authToken,
        Math.max(POLL_MAX_DURATION_MS - (Date.now() - startTime), 1000),
      );
      const status = data?.status;

      if (status === 'active' || status === 'paid' || status === 'trialing') {
        return { outcome: 'success', isTrial: status === 'trialing' };
      }
      if (status === 'failed') {
        return { outcome: 'failed' };
      }
    } catch {
      return { outcome: 'error' };
    }

    await new Promise((resolve) => {
      setTimeout(resolve, getPollIntervalMs(Date.now() - startTime));
    });
  }

  return isCancelled() ? null : { outcome: 'error' };
};

export type CheckoutSubmissionScreen = 'form' | 'processing' | 'success' | 'error';

export type UseCheckoutSubmissionParams = {
  productSelected: ProductSelected;
  // catálogo/convênio/tier/modo da seleção (ExplorePlansModal/ComparePlansModal),
  // ausente quando a origem não passou por lá (ex.: initialProductSelected).
  trackingProperties?: UpgradeTrackingProperties | null;
  paymentMethodMode: CheckoutPaymentMethodMode;
  rawSavedCard: CheckoutSavedCard | null;
  newCardValues: CreditCardValidationValues;
  recaptchaToken?: string;
  jusgptLimitFunnel?: JusgptLimitFunnel;
  onCheckoutSuccess?: () => void;
};

type CreateSubscriptionResponse = {
  status?: string;
  data?: { id?: string; status?: string };
};

const splitExpiration = (cardExpiration: string): { expMonth: string; expYear: string } => {
  const digits = cardExpiration.replace(/\D/g, '');
  return { expMonth: digits.slice(0, 2), expYear: digits.slice(2, 4) };
};

export function useCheckoutSubmission({
  productSelected,
  trackingProperties,
  paymentMethodMode,
  rawSavedCard,
  newCardValues,
  recaptchaToken,
  jusgptLimitFunnel,
  onCheckoutSuccess,
}: UseCheckoutSubmissionParams) {
  const dispatch = useDispatch();
  const refreshSubscriptionStatus = useRefreshSubscriptionStatus();
  const authToken = useSelector((state: { auth: { authToken: string } }) => state.auth.authToken);
  const user = useSelector(
    (state: { auth: { user: { id?: string | number } | null } }) => state.auth.user,
  );
  const currentPlan = useSelector(
    (state: { subscription: { subscription_status: unknown } }) =>
      state.subscription.subscription_status,
  );
  const [screen, setScreen] = useState<CheckoutSubmissionScreen>('form');
  const [isTrialSuccess, setIsTrialSuccess] = useState(false);
  // Mirrors pendingSubscriptionIdRef for the error-screen copy (ref alone does not re-render).
  const [isPendingConfirmation, setIsPendingConfirmation] = useState(false);
  const isSubmittingRef = useRef(false);
  const isUnmountedRef = useRef(false);
  const pendingSubscriptionIdRef = useRef<string | null>(null);
  const hasHandledCreateCallbackRef = useRef(false);

  const clearPendingSubscription = () => {
    pendingSubscriptionIdRef.current = null;
    setIsPendingConfirmation(false);
  };

  const setPendingSubscription = (subscriptionId: string) => {
    pendingSubscriptionIdRef.current = subscriptionId;
    setIsPendingConfirmation(true);
  };

  useEffect(() => () => {
    isUnmountedRef.current = true;
  }, []);

  const resumePolling = (subscriptionId: string) => {
    const url = window.location.pathname.split('/')[1];
    const paymentMethodInfo = {
      type: 'card',
      card_number: paymentMethodMode === 'new' ? newCardValues.card_number : '',
    };

    isSubmittingRef.current = true;
    setScreen('processing');

    pollSubscriptionStatus(subscriptionId, authToken, () => isUnmountedRef.current)
      .then((result) => {
        isSubmittingRef.current = false;
        if (isUnmountedRef.current || !result) {
          return;
        }
        if (result.outcome === 'success') {
          setIsTrialSuccess(result.isTrial);
          setScreen('success');
          clearPendingSubscription();

          try {
            trackUpgradePaymentSuccess({
              ...tracking.upgrade.prepareUpgradePaymentSuccessForAnalytics(
                user,
                currentPlan,
                productSelected,
                { status: result.isTrial ? 'trialing' : 'active', id: subscriptionId },
                paymentMethodInfo,
                url,
              ),
              ...trackingProperties,
            });
          } catch (error) {
            captureCheckoutException('checkout_success_analytics', error, { subscriptionId });
          }
          try {
            refreshSubscriptionStatus();
          } catch (error) {
            captureCheckoutException('checkout_success_refresh_subscription', error, {
              subscriptionId,
            });
          }
          try {
            notifyJusGPTUpgradeSuccess(authToken);
          } catch (error) {
            captureCheckoutException('checkout_success_jusgpt_notify', error, { subscriptionId });
          }
          try {
            if (jusgptLimitFunnel) {
              trackJusGPTUpgradeCompleted({
                planOrigem: jusgptLimitFunnel.plan,
                planDestino: productSelected.name || String(productSelected.id),
                diasAteUpgrade: consumeJusGPTDaysUntilUpgrade(user?.id),
              });
            }
            onCheckoutSuccess?.();
          } catch (error) {
            captureCheckoutException('checkout_success_jusgpt_funnel', error, { subscriptionId });
          }
          return;
        }
        if (result.outcome === 'failed') {
          clearPendingSubscription();
        }
        trackUpgradePaymentFailed({
          ...tracking.upgrade.prepareUpgradePaymentFailedForAnalytics(
            user,
            currentPlan,
            productSelected,
            { message: 'Falha ao confirmar o status da assinatura' },
            paymentMethodInfo,
            url,
          ),
          ...trackingProperties,
        });
        setScreen('error');
      })
      .catch((error) => {
        captureCheckoutException('checkout_polling_then_handler', error, { subscriptionId });
        isSubmittingRef.current = false;
        if (isUnmountedRef.current) {
          return;
        }
        setScreen('error');
      });
  };

  const submit = async () => {
    if (isSubmittingRef.current) {
      return;
    }

    if (pendingSubscriptionIdRef.current) {
      resumePolling(pendingSubscriptionIdRef.current);
      return;
    }

    isSubmittingRef.current = true;
    hasHandledCreateCallbackRef.current = false;
    setScreen('processing');

    const url = window.location.pathname.split('/')[1];
    const paymentMethodInfo = {
      type: 'card',
      card_number: paymentMethodMode === 'new' ? newCardValues.card_number : '',
    };

    try {
      trackPlanUpgraded({
        ...tracking.upgrade.preparePlanUpgradedForAnalytics(
          user,
          currentPlan,
          productSelected,
          paymentMethodInfo,
          url,
        ),
        ...trackingProperties,
      });
      trackCheckoutSubscribeClicked(productSelected.plan_type);

      let cardHash: string | undefined;

      if (paymentMethodMode === 'new') {
        try {
          const { expMonth, expYear } = splitExpiration(newCardValues.card_expiration);
          const token = await tokenizeCardWithTimeout({
            holder_name: newCardValues.cardholder_name,
            number: newCardValues.card_number,
            exp_month: expMonth,
            exp_year: expYear,
            cvv: newCardValues.card_cvv,
          });
          cardHash = token.id;
          trackUpgradeCardTokenizationSuccess(
            tracking.upgrade.prepareUpgradeCardTokenizationSuccessForAnalytics(
              user,
              currentPlan,
              productSelected,
              token,
              { card_number: newCardValues.card_number },
              url,
            ),
          );
        } catch (error) {
          if (isUnmountedRef.current) {
            return;
          }
          captureCheckoutException('checkout_card_tokenization', error);
          trackUpgradeCardTokenizationError(
            tracking.upgrade.prepareUpgradeCardTokenizationErrorForAnalytics(
              user,
              currentPlan,
              productSelected,
              {
                message: 'Erro ao processar o cartão',
                code: 'TOKENIZATION_ERROR',
                type: 'tokenization_error',
              },
              { card_number: newCardValues.card_number },
              url,
            ),
          );
          isSubmittingRef.current = false;
          setScreen('error');
          return;
        }
      }

      if (isUnmountedRef.current) {
        return;
      }

      dispatch({
        type: 'CREATE_SUBSCRIPTION',
        payload: {
          values: {
            product_selected: productSelected,
            card_selected: paymentMethodMode === 'saved' ? rawSavedCard : null,
            card_hash: cardHash,
            recaptchaToken,
            ignoreAdminCheckout: true,
          },
          callback: (response: CreateSubscriptionResponse) => {
            if (hasHandledCreateCallbackRef.current) {
              return;
            }
            hasHandledCreateCallbackRef.current = true;

            const subscriptionId = response?.data?.id;

            if (!subscriptionId) {
              if (isUnmountedRef.current) {
                return;
              }
              try {
                clearPendingSubscription();
                captureCheckoutException(
                  'checkout_create_subscription',
                  new Error('Create subscription failed or returned without ID'),
                  sanitizeCreateSubscriptionResponseForSentry(response),
                );
                trackUpgradePaymentFailed({
                  ...tracking.upgrade.prepareUpgradePaymentFailedForAnalytics(
                    user,
                    currentPlan,
                    productSelected,
                    response,
                    paymentMethodInfo,
                    url,
                  ),
                  ...trackingProperties,
                });
              } catch (error) {
                captureCheckoutException('checkout_create_failure_side_effects', error);
              } finally {
                isSubmittingRef.current = false;
                setScreen('error');
              }
              return;
            }

            try {
              setPendingSubscription(subscriptionId);

              if (isUnmountedRef.current) {
                return;
              }

              resumePolling(subscriptionId);
            } catch (error) {
              captureCheckoutException('checkout_create_success_side_effects', error, {
                subscriptionId,
              });
              if (isUnmountedRef.current) {
                return;
              }
              isSubmittingRef.current = false;
              setScreen('error');
            }
          },
          setSubmitting: () => {},
        },
      });
    } catch (error) {
      if (isUnmountedRef.current) {
        return;
      }
      captureCheckoutException('checkout_submit', error);
      isSubmittingRef.current = false;
      setScreen('error');
    }
  };

  const retry = () => {
    isSubmittingRef.current = false;
    if (pendingSubscriptionIdRef.current) {
      resumePolling(pendingSubscriptionIdRef.current);
      return;
    }
    setScreen('form');
  };

  return { screen, isTrialSuccess, isPendingConfirmation, submit, retry };
}
